import { createFileRoute, useParams } from '@tanstack/react-router'
import { Button, Center, Text } from '@chakra-ui/react'
import { FaTriangleExclamation } from 'react-icons/fa6'
import { useEffect, useState } from 'react'
import MediaSoupCall from '@features/commentary/components/Participant/MediaSoupCall'
import { SessionEndScreen } from '@features/commentary/components/PublicParticipant/SessionEndScreen'
import { PreJoinCard } from '@features/commentary/components/PreJoinCard'
import { Shell } from '@features/commentary/components/PermissionRecovery'
import { AuthenticationContainer } from '@features/lobby/components/AuthenticationContainer'
import { GuardPermissionState } from '@features/commentary/components/PermissionsGuard'
import { buildLobbyMediaConstraints } from '@features/commentary/mediaConstraints'
import {
  getLobbyRouteKey,
  LobbyPhase,
  useLobbyStore,
} from '@features/commentary/store/lobbyStore'
import {
  UserMediaProvider,
  useUserMediaContext,
  type PermissionState,
} from '@hooks/userMedia'

const derivePermissionState = (
  stream: MediaStream | null,
  error: string | null,
  permissionState?: PermissionState
): GuardPermissionState => {
  if (stream) return GuardPermissionState.Granted
  if (permissionState === 'denied') return GuardPermissionState.Denied
  if (permissionState === 'granted') return GuardPermissionState.Granted
  if (error) return GuardPermissionState.Denied
  return GuardPermissionState.Prompt
}

interface LobbyPageProps {
  token: string
  projectId: string
}

const LobbyPage = ({ token, projectId }: LobbyPageProps) => {
  const lobbyRouteKey = getLobbyRouteKey(projectId, token)
  const [lobbyCredentials, setLobbyCredentials] = useState<null | {
    token: string
    hermesRouterAddress: string
    workflowSessionId: string
  }>(null)
  const activeLobbyKey = useLobbyStore((s) => s.activeLobbyKey)
  const phase = useLobbyStore((s) => s.phase)
  const enterLobby = useLobbyStore((s) => s.enterLobby)
  const setPhase = useLobbyStore((s) => s.setPhase)
  const setError = useLobbyStore((s) => s.setError)
  const errorMessage = useLobbyStore((s) => s.errorMessage)
  const reset = useLobbyStore((s) => s.reset)
  const { stream, error, refresh, permissionState, errorKind, status } =
    useUserMediaContext()

  const isCurrentLobby = activeLobbyKey === lobbyRouteKey
  const currentPhase = isCurrentLobby ? phase : LobbyPhase.Checking

  useEffect(() => {
    enterLobby(lobbyRouteKey)
    setLobbyCredentials(null)
  }, [enterLobby, lobbyRouteKey])

  if (currentPhase === LobbyPhase.InCall && lobbyCredentials !== null) {
    return (
      <MediaSoupCall
        workflowSessionId={lobbyCredentials.workflowSessionId}
        handleLeave={() => {
          setPhase(LobbyPhase.Left)
        }}
        lobbyCredentials={lobbyCredentials}
      />
    )
  }

  if (currentPhase === LobbyPhase.Left) {
    return (
      <SessionEndScreen
        handleClick={() => {
          reset()
          refresh()
          setLobbyCredentials(null)
        }}
      />
    )
  }

  if (currentPhase === LobbyPhase.Error) {
    return (
      <Center h="100vh" w="100%" p={4}>
        <Shell
          icon={FaTriangleExclamation}
          title="Couldn't connect to the session"
        >
          <Text color="muted">
            {isCurrentLobby && errorMessage
              ? errorMessage
              : 'Something went wrong. Please try rejoining.'}
          </Text>
          <Button
            colorScheme="brand"
            variant="solid"
            size="sm"
            alignSelf="flex-start"
            onClick={() => {
              setError(null)
              reset()
              refresh()
              setLobbyCredentials(null)
            }}
          >
            Rejoin
          </Button>
        </Shell>
      </Center>
    )
  }

  return (
    <PreJoinCard
      stream={stream}
      permissionState={derivePermissionState(stream, error, permissionState)}
      errorKind={error ? errorKind : undefined}
      errorDetail={error}
      mediaStatus={status}
      onRetry={refresh}
      footer={
        <AuthenticationContainer
          token={token}
          projectId={projectId}
          setLobbyCredentials={setLobbyCredentials}
        />
      }
    />
  )
}

const Lobby = ({ token, projectId }: LobbyPageProps) => {
  const videoDeviceId = useLobbyStore((s) => s.videoDeviceId)
  const audioDeviceId = useLobbyStore((s) => s.audioDeviceId)
  const videoHeight = useLobbyStore((s) => s.videoHeight)
  const echoCancellation = useLobbyStore((s) => s.echoCancellation)
  const noiseSuppression = useLobbyStore((s) => s.noiseSuppression)
  const autoGainControl = useLobbyStore((s) => s.autoGainControl)

  return (
    <UserMediaProvider
      constraints={buildLobbyMediaConstraints({
        videoDeviceId,
        audioDeviceId,
        videoHeight,
        echoCancellation,
        noiseSuppression,
        autoGainControl,
      })}
    >
      <LobbyPage token={token} projectId={projectId} />
    </UserMediaProvider>
  )
}

const LobbyRoute = () => {
  const { projectId, token } = useParams({
    from: '/lobby/$projectId/$token',
  })

  return <Lobby token={token} projectId={projectId} />
}

export const Route = createFileRoute('/lobby/$projectId/$token')({
  component: LobbyRoute,
})
